import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, app } from '../protected/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Dialog } from '@headlessui/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FaExclamationTriangle } from 'react-icons/fa';

export default function Dashboard() {
  const { user, companyId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [goalModal, setGoalModal] = useState(false);
  const [salesGoal, setSalesGoal] = useState(0);
  const [goalInput, setGoalInput] = useState('');
  const [todayTotal, setTodayTotal] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [netProfit, setNetProfit] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [showLowStockWarning, setShowLowStockWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalBundleLoss, setTotalBundleLoss] = useState(0);
  const [companyName, setCompanyName] = useState('Your Company');
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    plan: '',
    status: '',
    trialEnds: null,
  });
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      if (!user || !companyId) {
        setLoading(false);
        return;
      }
      
      // Load company profile
      const companyProfileRef = doc(db, 'companies', companyId, 'companyProfile', 'info');
      const companySnap = await getDoc(companyProfileRef);
      if (companySnap.exists()) {
        setCompanyName(companySnap.data().companyName || 'Your Company');
      }

      // Load subscription info from company root doc
      const companyRef = doc(db, 'companies', companyId);
      const companyDoc = await getDoc(companyRef);
      const subscription = companyDoc.exists() ? companyDoc.data()?.subscription || {} : {};
      const trialEnds = subscription.trialEnds?.toDate ? subscription.trialEnds.toDate() : (subscription.trialEnds ? new Date(subscription.trialEnds) : null);
      setSubscriptionInfo({
        plan: subscription.plan || 'Free Trial',
        status: subscription.status || '',
        trialEnds,
      });
      if (trialEnds) {
        const now = new Date();
        const diff = Math.ceil((trialEnds.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        setTrialDaysLeft(Math.max(diff, 0));
      } else {
        setTrialDaysLeft(null);
      }
      // Check and refresh subscription
      await checkAndRefreshSubscription(companyId);
      await fetchRevenue();
      await loadData();
      await loadGoal();
      setLoading(false);
    };
    const checkAndRefreshSubscription = async (companyId) => {
            try {
              const functions = getFunctions(app);
              const result = await httpsCallable(functions, 'getLatestSubscription')({companyId});
              if (result?.data?.status) {
                console.log("Subscription refreshed:", result.data);
              }
            } catch (err) {
              console.error("Failed to refresh subscription:", err.message);
            }
          };
    
          checkAndRefreshSubscription(companyId);
    loadAll();
  }, [companyId]);

  const isTrialing = subscriptionInfo?.status === 'trialing' || (subscriptionInfo?.plan || '').toLowerCase().includes('trial');

  const fetchRevenue = async () => {
    const q = query(collection(db, 'companies', companyId, 'orders'), where('paymentStatus', '!=', 'Void'));
    const snap = await getDocs(q);
    const orders = snap.docs.map(doc => doc.data());

    const monthlyTotals = Array.from({ length: 12 }, (_, i) => ({
      month: new Date(0, i).toLocaleString('default', { month: 'long' }),
      total: 0,
    }));

    for (let order of orders) {
      const date = order.timestamp?.toDate?.() || new Date(order.timestamp);
      const monthIndex = date.getMonth();
      monthlyTotals[monthIndex].total += Number(order.total || 0);
    }

    setMonthlyRevenue(monthlyTotals);
  };

  const loadData = async () => {
    const orderSnap = await getDocs(query(collection(db, 'companies', companyId, 'orders'), where('paymentStatus', '!=', 'Void')));
    const ordersData = orderSnap.docs.map(doc => doc.data());
    setOrders(ordersData);
    setCustomerCount(ordersData.length);
    
    // Fetch both products and bundles
    const [productSnap, bundleSnap] = await Promise.all([
      getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'products')),
      getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'bundles'))
    ]);
    
    const productData = productSnap.docs.map(doc => ({ 
      ...doc.data(), 
      type: 'product',
      // Ensure consistent field names
      sku: doc.data().sku || '-',
      qty: doc.data().qty || 0
    }));
    
    const bundleData = bundleSnap.docs.map(doc => ({
      ...doc.data(),
      type: 'bundle',
      // Map bundle fields to match product fields
      sku: doc.data().sku || `BNDL-${doc.id}`, // Add prefix for bundles if SKU is missing
      qty: doc.data().quantity || 0, // Map 'quantity' to 'qty' for consistency
      // Keep original quantity field for bundle calculations
      originalQuantity: doc.data().quantity || 0
    }));
    
    // Combine products and bundles for inventory data
    const allItems = [...productData, ...bundleData];
    setProducts(allItems);

    let revenue = 0, totalDiscount = 0, sst6Amount = 0, sst8Amount = 0, serviceTax10Amount = 0, rounding = 0;
    let inventoryCost = 0;
    let cogsProduct = 0;
    let cogsBundle = 0;
    let cogs = 0;
    let todayRevenue = 0;
    const today = new Date().toISOString().slice(0, 10);
    const productSalesMap = {};
    let totalBundleLoss = 0;
    
    
    ordersData.forEach(order => {
      const date = (order.timestamp?.toDate?.() || new Date(order.timestamp)).toISOString().slice(0, 10);
      revenue += Number(order.total || 0);
      totalDiscount += Number(order.discountAmount || 0);
      sst6Amount += Number(order.sst6Amount || 0);
      sst8Amount += Number(order.sst8Amount || 0);
      serviceTax10Amount += Number(order.serviceTax10Amount || 0);
      rounding += Number(order.rounding || 0);
      if (date === today) todayRevenue += Number(order.total || 0);
    
      order.items?.forEach(item => {
        totalBundleLoss += Number(item.bundleLoss || 0);
        cogsBundle += Number(item.totalProductPurchase || 0);
        // Track both products and bundles in sales
        if (!productSalesMap[item.name]) {
          productSalesMap[item.name] = { 
            sku: item.sku || '-', 
            qty: 0,
            type: item.type || 'product' // Track if it's a product or bundle
          };
        }
        productSalesMap[item.name].qty += item.qty || 0;
    
        // Calculate COGS for each sold item
        if (!item.sku || !item.qty) return;
        
        // Check both products and bundles for COGS calculation
        const itemData = allItems.find(p => p.sku === item.sku || p.id === item.id);
        if (itemData) {
          const cost = itemData.type === 'bundle' 
            ? (itemData.products || []).reduce((sum, p) => {
                const product = productData.find(prod => prod.sku === p.sku || prod.id === p.id);
                return sum + (product?.purchase || 0) * (p.quantity || p.qty || 0);
              }, 0)
            : itemData.purchase || 0;
              
          cogsProduct += Number(cost) * Number(item.qty || item.quantity || 0);
        }
      });
    });
    const totalTax = sst6Amount + sst8Amount + serviceTax10Amount;
    
    // You can keep inventoryCost calculation for asset reporting if needed
    productData.forEach(product => {
      inventoryCost += Number(product.purchase || 0) * Number(product.qty || 0);
    });
    
    // Update gross profit calculation to use COGS
    const grossProfit = revenue - cogsProduct - cogsBundle;
    const calculatedProfit = grossProfit - totalDiscount - totalBundleLoss - totalTax + rounding;

    const combined = [];

    // Add base products (exclude those that have variants so variants will represent them)
    productData.forEach(p => {
      const hasVariants = Array.isArray(p.variants) && p.variants.length > 0;
      const qtyVal = Number(p.qty || 0);
      if (!hasVariants && qtyVal <= 10) {
        combined.push({
          id: p.id,
          sku: p.sku || '-',
          name: p.name,
          qty: qtyVal,
          variants: [],
        });
      }
    });

    // Add variants grouped under their parent
    productData.forEach(p => {
      const variants = Array.isArray(p.variants) ? p.variants : [];
      const lowVariants = variants
        .filter(v => Number(v.qty || 0) <= 10)
        .map(v => {
          const label = (Array.isArray(v.options) && v.options.length > 0)
            ? v.options.map(o => `${o.group}: ${o.value}`).join(' / ')
            : v.name || v.label || 'Variant';
          return {
            id: v.id || v.variantId,
            sku: v.sku || v.variantSku || v.barcode || p.sku || '-',
            label,
            qty: Number(v.qty || 0),
          };
        });
      if (lowVariants.length) {
        combined.push({
          id: p.id,
          sku: p.sku || '-',
          name: p.name,
          qty: Number(p.qty || 0),
          variants: lowVariants,
        });
      }
    });

    // Add bundles if low
    bundleData.forEach(b => {
      const qtyVal = Number(b.quantity || 0);
      if (qtyVal <= 10) {
        combined.push({
          id: b.id,
          sku: b.sku || '-',
          name: b.name + ' (Bundle)',
          qty: qtyVal,
          variants: [],
        });
      }
    });

    const ordered = combined
      .sort((a, b) => {
        const minA = a.variants.length ? Math.min(...a.variants.map(v => v.qty)) : a.qty;
        const minB = b.variants.length ? Math.min(...b.variants.map(v => v.qty)) : b.qty;
        return minA - minB;
      })
      .slice(0, 10);

    setLowStockItems(ordered);
    
    // Show warning and play sound if there are low stock items
    if (ordered.length > 0) {
      setShowLowStockWarning(true);
      
      // Auto-hide warning after 10 seconds
      setTimeout(() => {
        setShowLowStockWarning(false);
      }, 10000);
    } else if (ordered.length === 0) {
      setShowLowStockWarning(false);
    }

    setTotalRevenue(revenue);
    setNetProfit(calculatedProfit);
    setTodayTotal(todayRevenue);
  };

  const loadGoal = async () => {
    const snap = await getDoc(doc(db, 'companies', companyId, 'companySettings', 'dashboard'));
    if (snap.exists()) {
      const data = snap.data();
      setSalesGoal(data.dailyGoal || 0);
    }
  };

  const saveGoal = async () => {
    await setDoc(doc(db, 'companies', companyId, 'companySettings', 'dashboard'), {
      dailyGoal: Number(goalInput),
    });
    setSalesGoal(Number(goalInput));
    setGoalModal(false);
  };

  if (loading) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="w-full px-4 pt-2 pb-6 sm:px-6 sm:pt-0 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">
          Welcome to {companyName || 'Your Company'}, {user?.displayName || 'User'}!
        </h1>
        <p className="text-gray-400 mt-2">Here's what's happening with your store today</p>
        {isTrialing && trialDaysLeft !== null && (
          <div className="mt-3 inline-flex items-center gap-3 bg-yellow-900/30 border border-yellow-700/40 text-yellow-100 px-4 py-2 rounded-lg">
            <span className="text-xs font-semibold bg-yellow-700/70 text-black px-2 py-1 rounded">Trial</span>
            <div className="text-sm">
              Account in trial — {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid (scrollable on small screens) */}
      <div className="overflow-x-auto pb-2 -mx-2 sm:mx-0 mb-8">
        <div className="inline-flex gap-4 px-2 sm:px-0 w-max sm:w-auto sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-5">
          {/* Revenue Card */}
          <div className="min-w-[240px] sm:min-w-0 bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-xl border border-gray-700 shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <img src="icons/cash.png" alt="Revenue" className="w-10 h-10" />
              <span className="text-sm text-gray-400">Total</span>
            </div>
            <h2 className="text-2xl text-white font-bold mb-1">RM {totalRevenue.toFixed(2)}</h2>
            <p className="text-sm text-gray-400">Total Revenue</p>
          </div>

          {/* Profit/Loss Card */}
          <div className="min-w-[240px] sm:min-w-0 bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-xl border border-gray-700 shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <img 
                src={netProfit >= 0 ? 'icons/profit.png' : 'icons/loss.png'} 
                alt="Profit/Loss" 
                className="w-10 h-10" 
              />
              <span className="text-sm text-gray-400">{netProfit >= 0 ? 'Profit' : 'Loss'}</span>
            </div>
            <h2 className={`text-2xl font-bold mb-1 ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {netProfit >= 0 ? '+' : ''}RM {Math.abs(netProfit).toFixed(2)}
            </h2>
            <p className="text-sm text-gray-400">Net {netProfit >= 0 ? 'Profit' : 'Loss'}</p>
          </div>

          {/* Daily Goal Card */}
          <div 
            className="min-w-[240px] sm:min-w-0 bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-xl border border-gray-700 shadow-lg hover:shadow-purple-500/20 transition-all duration-300 cursor-pointer"
            onClick={() => {
              setGoalInput(salesGoal.toString());
              setGoalModal(true);
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <img 
                src={todayTotal >= salesGoal ? 'icons/goalAchived.png' : 'icons/goal.png'} 
                alt="Goal" 
                className="w-10 h-10" 
              />
              <span className="text-sm text-gray-400">Target: {((todayTotal/salesGoal)*100 || 0).toFixed(0)}%</span>
            </div>
            <h2 className={`text-2xl font-bold mb-1 ${todayTotal >= salesGoal ? 'text-green-400' : 'text-red-400'}`}>
              RM {todayTotal.toFixed(2)}
            </h2>
            <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
              <div 
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full" 
                style={{ width: `${Math.min(100, (todayTotal/salesGoal)*100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-400 mt-1">Daily Goal: RM {salesGoal.toFixed(2)}</p>
          </div>

          {/* Customers Card */}
          <div className="min-w-[240px] sm:min-w-0 bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-xl border border-gray-700 shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <img src="icons/customer.png" alt="Customers" className="w-10 h-10" />
              <span className="text-sm text-gray-400">Total</span>
            </div>
            <h2 className="text-2xl font-bold text-yellow-400 mb-1">{customerCount}</h2>
            <p className="text-sm text-gray-400">Total Customers</p>
          </div>
        </div>
      </div>

      {/* Low Stock Warning */}
      {showLowStockWarning && (
        <div className="bg-gradient-to-r from-yellow-600/30 to-yellow-800/30 border border-yellow-500/30 text-white p-4 rounded-lg mb-6 flex items-center justify-between backdrop-blur-sm animate-pulse">
          <div className="flex items-center">
            <FaExclamationTriangle className="text-yellow-400 mr-3 text-xl" />
            <span>You have <span className="font-bold">{lowStockItems.length} item(s)</span> low in stock!</span>
          </div>
          <button 
            onClick={() => setShowLowStockWarning(false)}
            className="text-white/70 hover:text-white text-lg px-2"
          >
            ×
          </button>
        </div>
      )}

      {/* Revenue Chart */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-gray-700 shadow-lg mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">Revenue Overview</h3>
            <p className="text-sm text-gray-400">Monthly revenue performance</p>
          </div>
          <span className="text-xs px-3 py-1 bg-gray-700/50 text-gray-300 rounded-full">Last 12 months</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyRevenue}>
              <defs>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#9e62ff" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#9e62ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="month" 
                stroke="#4B5563" 
                tick={{ fill: '#9CA3AF', fontSize: 12 }} 
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#4B5563" 
                tick={{ fill: '#9CA3AF', fontSize: 12 }} 
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `RM${value}`}
              />
              <Tooltip 
                formatter={value => [`RM ${value.toFixed(2)}`, 'Revenue']}
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }} 
                labelStyle={{ color: '#E5E7EB', fontWeight: 500 }}
                itemStyle={{ color: '#E5E7EB' }}
              />
              <Area 
                type="monotone" 
                dataKey="total" 
                stroke="#9e62ff" 
                fill="url(#lineGradient)" 
                strokeWidth={2} 
                dot={{ fill: '#9e62ff', strokeWidth: 2, r: 3 }}
                activeDot={{ fill: '#ffffff', stroke: '#9e62ff', strokeWidth: 2, r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Low Stock Items */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-gray-700 shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">Low Stock Alert</h3>
            <p className="text-sm text-gray-400">Items that need restocking</p>
          </div>
          <span className="text-xs px-3 py-1 bg-gray-700/50 text-gray-300 rounded-full">{lowStockItems.length} items</span>
        </div>
        
        {lowStockItems.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-800/50">
                <tr className="text-left text-gray-400">
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium">Item</th>
                  <th className="px-4 py-3 font-medium text-right">In Stock</th>
                  <th className="px-4 py-3 font-medium text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {lowStockItems.map((item, i) => {
                  const baseCritical = item.qty <= 3;
                  return (
                    <React.Fragment key={i}>
                      <tr className="hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 font-mono text-purple-300 align-top">{item.sku || '-'}</td>
                        <td className="px-4 py-3 text-white align-top">{item.name}</td>
                        <td className={`px-4 py-3 text-right font-medium align-top ${baseCritical ? 'text-red-400' : 'text-yellow-400'}`}>
                          {item.qty}
                        </td>
                        <td className="px-4 py-3 text-center align-top">
                          {item.variants && item.variants.length > 0 ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-900/30 text-blue-300 border border-blue-800/50">
                              Variants
                            </span>
                          ) : (
                            (baseCritical ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-900/30 text-red-300 border border-red-800/50">
                                <FaExclamationTriangle className="mr-1.5" /> Critical
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-900/30 text-yellow-300 border border-yellow-800/50">
                                Low Stock
                              </span>
                            ))
                          )}
                        </td>
                      </tr>
                      {item.variants && item.variants.map((v, idx) => {
                        const vCritical = v.qty <= 3;
                        return (
                          <tr key={`${i}-${idx}`} className="bg-gray-900/30">
                            <td className="px-8 py-2 font-mono text-purple-300 align-top">{v.sku || item.sku || '-'}</td>
                            <td className="px-8 py-2 text-gray-300 align-top">↳ {v.label}</td>
                            <td className={`px-4 py-2 text-right font-medium align-top ${vCritical ? 'text-red-400' : 'text-yellow-400'}`}>
                              {v.qty}
                            </td>
                            <td className="px-4 py-2 text-center align-top">
                              {vCritical ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-900/30 text-red-300 border border-red-800/50">
                                  <FaExclamationTriangle className="mr-1.5" /> Critical
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-900/30 text-yellow-300 border border-yellow-800/50">
                                  Low Stock
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="inline-block p-4 bg-gray-800/50 rounded-full mb-3">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-200">All items are well-stocked!</h4>
            <p className="text-gray-400 text-sm mt-1">No low stock items to display</p>
          </div>
        )}
        
        {lowStockItems.length > 0 && (
          <div className="mt-4 text-xs text-gray-500 text-right">
            Showing items with 10 or less in stock
          </div>
        )}
      </div>

      {/* Goal Setting Modal */}
      <Dialog open={goalModal} onClose={() => setGoalModal(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 p-6 border border-gray-700 shadow-2xl">
            <Dialog.Title className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent mb-2">
              Set Daily Sales Goal
            </Dialog.Title>
            <p className="text-sm text-gray-400 mb-6">Enter your target sales amount for today.</p>
            
            <div className="mb-6">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">RM</span>
                <input
                  type="number"
                  value={goalInput}
                  onChange={e => setGoalInput(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  onFocus={e => e.target.select()}
                  autoFocus
                />
              </div>
              
              <div className="mt-4 grid text-white grid-cols-4 gap-2">
                {[100, 500, 1000, 5000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setGoalInput(amount.toString())}
                    className="px-3 py-2 text-sm bg-gray-700/50 hover:bg-gray-600/50 rounded-md transition-colors"
                  >
                    RM {amount}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setGoalModal(false)} 
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={saveGoal} 
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg transition-colors"
              >
                Save Goal
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
